import { Observable } from 'rxjs';

import { GridSync, IIdentity, SortDescriptor } from '.';

export interface GridApi<T extends IIdentity> {
  /**
   * @description Adds the given rows as new rows to the grid and tracks them as added in the changes model.
   * *DO NOT use this method to add rows that are already saved on the server.
   * To add already saved rows to the grid, please add it directly to the data input.
   *
   * A new id is automatically assigned in order to be able to track further changes on the row.
   * A priority is automatically set in order to facilitate the sorting.
   *
   * > `addConfig.allowAdding` should be true.
   *
   * @param data Defines the new add rows data.
   */
  addRows: (...dataList: T[]) => void;

  /**
   * @description Insert the given rows at the given position as new rows to the grid
   * and tracks them as added in the changes model.
   *
   * A new id is automatically assigned in order to be able to track further changes on the row.
   * A priority is automatically set in order to facilitate the sorting.
   *
   *  > `addConfig.allowAdding` should be true.
   *
   * @param dataList Defines the new rows.
   * @param indexes Defines the indexes on which the rows should be inserted
   */
  insertRows: (dataList: T[], indexes: number[]) => void;

  /**
   * @description Updates the given row in the grid and tracks it as modified in the changes model.
   *
   * @param data Defines the row data to be updated.
   *
   * @returns Returns true if the row was updated, false if there were no visible changes.
   */
  updateRow: (data: T) => boolean;

  /**
   * @description Deletes the given rows
   *
   * @param data Defines the rows to be deleted.
   */
  deleteRows: (...data: T[]) => void;

  /**
   * @description Edits a cell so that grid can track the changes and mark the cell as changed
   */
  editCell: (data: T, columnDef: string, value: any) => void;

  /**
   * @description Gets the current changes of the grid
   * Filters out the empty rows from the added changes
   */
  getChanges: () => GridSync<T>;

  /**
   * @desciption Gets the current changes of the grid
   * Filter out the empty rows from the added changes
   * Updates priorities of the non-empty rows
   */
  getChangesWithUpdatedPriorities: () => GridSync<T>;

  /**
   * @description Check if the table has been modified by the user
   */
  isDirty: () => boolean;

  /**
   * @description Emits once grid is modified by the user
   */
  isDirty$: Observable<boolean>;

  /**
   * @description Checks if there are changes of the grid
   * Returns true even if the rows are empty
   */
  hasChanges: () => boolean;

  /**
   * @description Observable that emits every time a change happens on the grid
   *  Emits true even if the rows are empty
   */
  hasChanges$: Observable<boolean>;

  /**
   * @description Checks if the property is marked as changed
   */
  isPropertyChanged: (data: T, property: string) => boolean;

  /**
   * @description Gets the current items selected by the grid
   */
  getSelection: () => T[];

  /**
   * @description Observable that emits every time a new selection happens on the grid
   * Emits all the current selected values
   */
  selected$: Observable<T[]>;

  /**
   * @description Sets the given changes to the grid
   */
  setPredefinedChanges: (gridSync: GridSync<T>) => void;

  /**
   * @description Removes the given changes from the current grid changes
   */
  removeChanges: (changes: string[]) => void;

  /**
   * @description Start the internal process of saving grid changes
   */
  save: () => Observable<T[]>;

  /**
   * @description Resets grid to initial state by clearing all the changes
   */
  reset: () => void;

  /**
   * @description Recomputes all grid cells
   */
  repaintRows: () => void;

  /**
   * @description Recomputes a specific grid row by its id.
   *
   * @param rowId The id of the row to be repainted.
   */
  repaintRow: (rowId: number | string) => void;

  /**
   * @description Re-evaluates which rows can be selected, deselects the ones that no longer can, and re-runs each cell's
   * tab stop, focus classes and tooltip state. `repaintRows` does this on its own; call it after repainting rows one at
   * a time with `repaintRow`.
   */
  refreshSelectableRows: () => void;

  /**
   * @description Sort grid data based on sort mode
   */
  refreshSorting: () => void;
  /**
   * @description Select all rows
   */
  selectAll: () => void;

  /**
   * @description Scroll to the given data
   */
  scrollTo: (data: T) => void;

  /**
   * @description Scroll to the last row
   */
  scrollToLastRow: () => void;

  /**
   * @description Set sorting definition
   */
  setSortingDefinition: (definition: SortDescriptor<T>[]) => void;

  /**
   * @description Open edit programmatically
   */
  openEdit: (
    columnDef: string,
    id: number | string,
    additionalData?: any
  ) => void;

  /**
   * @description Refresh the filters
   */
  refreshFilters: () => void;

  rendering$: Observable<boolean>;
  setAllowAdding: (allowAdding: boolean) => void;
}
